/**
 * quick-buy-modal-custom - Extends modal-element with x-modal features (WITHOUT Shadow DOM)
 * Uses normal DOM like modal-element but with advanced x-modal functionality
 */

import ModalElement from 'modal';

(function () {
    'use strict';

    class QuickBuyModalCustom extends ModalElement {
        constructor() {
            super();
            this.productUrl = null;
            this._hasLoadedContent = false;
        }

        connectedCallback() {
            // Call the base connectedCallback from modal-element
            super.connectedCallback();

            this.setAttribute('role', 'dialog');
            this.setAttribute('aria-modal', 'true');
            this.productUrl = this.getAttribute('product-url');

            // Listen to modal:open event from modal-element to load content
            this.addEventListener('modal:open', async (e) => {
                if (this.productUrl && !this._hasLoadedContent) {
                    await this.loadProductContent();
                }
            });

            // Listen to variant:add event to close modal after adding to cart
            // This behavior replicates the original quick-buy-modal
            this.addEventListener('variant:add', (e) => {
                this.close();
            });
        }

        async open(trigger = null, triggerType = null) {
            // Load content before opening if not already loaded
            if (this.productUrl && !this._hasLoadedContent) {
                this.setAttribute('aria-busy', 'true');
                await this.loadProductContent();
                this.setAttribute('aria-busy', 'false');
            }

            // Lock body scroll (like x-modal)
            document.documentElement.classList.add('lock');

            // Call the open method from modal-element
            super.open(trigger, triggerType);
        }

        close() {
            // Unlock body scroll
            document.documentElement.classList.remove('lock');

            // Call the close method from modal-element
            super.close();

            // Clean content after closing
            setTimeout(() => {
                this.clearContent();
                this._hasLoadedContent = false;
            }, 300);
        }

        async loadProductContent() {
            const newProductUrl = this.getAttribute('product-url') || this.productUrl;
            this.productUrl = newProductUrl;

            if (!this.productUrl) return;

            try {
                const response = await fetch(this.productUrl);
                const html = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const quickBuyContent = doc.getElementById('quick-buy-content');

                if (quickBuyContent) {
                    const modalBody = this.querySelector('.modal__body');
                    if (modalBody) {
                        modalBody.innerHTML = '';
                        const content = quickBuyContent.content.cloneNode(true);
                        modalBody.appendChild(content);
                        this._hasLoadedContent = true;

                        this.dispatchEvent(new CustomEvent('product:loaded', {
                            bubbles: true,
                            detail: { modal: this, productUrl: this.productUrl }
                        }));

                        modalBody.querySelectorAll('[data-modal-close]').forEach(el => {
                            this.CLOSE_EVENTS.forEach(eventType => {
                                el.addEventListener(eventType, (event) => {
                                    if (eventType === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
                                        return;
                                    }
                                    this.close()
                                });
                            });
                        });
                    }
                }
            } catch (error) {
                console.error('Error loading product content:', error);
            }
        }

        clearContent() {
            if (this.productUrl) {
                const modalBody = this.querySelector('.modal__body');
                if (modalBody) {
                    modalBody.innerHTML = '';
                }
            }
        }

        // Backward compatibility with x-modal API
        async show(options = {}) {
            return this.open(options.activator);
        }

        async hide() {
            return this.close();
        }
    }

    if (!window.customElements.get('quick-buy-modal-custom')) {
        window.customElements.define('quick-buy-modal-custom', QuickBuyModalCustom);
    }
})();
